import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Stack,
  IconButton,
  Tooltip,
  Switch,
  CircularProgress,
  Link,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { categories as categoriesApi } from '../../../services/api';

const blankForm = {
  name: '',
  description: '',
  sortOrder: 0,
  isActive: true,
};

const CategoriesPage = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create mode
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsCat, setDetailsCat] = useState(null);
  const [detailsForm, setDetailsForm] = useState({ name: '', description: '' });
  const [detailsSaving, setDetailsSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await categoriesApi.list({ includeInactive: true });
      setList(data);
      setError(null);
    } catch (err) {
      console.error('Failed to load categories:', err);
      setError(err.response?.data?.error || 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(blankForm);
    setDialogOpen(true);
  };

  const openEdit = (cat) => {
    setEditing(cat);
    setForm({
      name: cat.name,
      description: cat.description || '',
      sortOrder: cat.sort_order || 0,
      isActive: !!cat.is_active,
    });
    setDialogOpen(true);
  };

  const openDetails = (cat) => {
    setDetailsCat(cat);
    setDetailsForm({
      name: cat.name,
      description: cat.description || '',
    });
    setDetailsOpen(true);
  };

  const handleDetailsSave = async () => {
    if (!detailsForm.name.trim()) {
      setError('Category name is required');
      return;
    }
    try {
      setDetailsSaving(true);
      const payload = {
        name: detailsForm.name.trim(),
        description: detailsForm.description.trim() || null,
      };
      await categoriesApi.update(detailsCat.id, payload);
      setInfo(`Updated "${payload.name}".`);
      setDetailsOpen(false);
      setDetailsCat(null);
      await load();
    } catch (err) {
      console.error('Save category details failed:', err);
      setError(err.response?.data?.error || 'Failed to save category');
    } finally {
      setDetailsSaving(false);
    }
  };

  const handleSave = async () => {
    if (!editing && !form.name.trim()) {
      setError('Category name is required');
      return;
    }
    try {
      setSaving(true);
      let payload;
      if (editing) {
        payload = {
          sortOrder: Number(form.sortOrder) || 0,
          isActive: form.isActive,
        };
      } else {
        payload = {
          name: form.name.trim(),
          description: form.description.trim() || null,
          sortOrder: Number(form.sortOrder) || 0,
          isActive: form.isActive,
        };
      }
      if (editing) {
        await categoriesApi.update(editing.id, payload);
        setInfo(`Updated settings for "${editing.name}".`);
      } else {
        await categoriesApi.create(payload);
        setInfo(`Created "${payload.name}".`);
      }
      setDialogOpen(false);
      setEditing(null);
      setForm(blankForm);
      await load();
    } catch (err) {
      console.error('Save category failed:', err);
      setError(err.response?.data?.error || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (cat) => {
    try {
      await categoriesApi.update(cat.id, { isActive: !cat.is_active });
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update category');
    }
  };

  const handleDelete = async (cat) => {
    const count = cat.vendor_count || 0;
    const msg = count > 0
      ? `"${cat.name}" is used by ${count} vendor${count === 1 ? '' : 's'}. It will be deactivated (hidden from new selections) but kept in the database for those vendors. Continue?`
      : `Delete "${cat.name}"? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      const result = await categoriesApi.remove(cat.id);
      setInfo(result.mode === 'deactivated'
        ? `"${cat.name}" was deactivated (it had assigned vendors).`
        : `"${cat.name}" was deleted.`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete category');
    }
  };

  return (
    <>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h4">Vendor Categories</Typography>
            <Typography variant="body2" color="text.secondary">
              The primary craft type a vendor selects on their profile (Wood Working, Stained Glass, etc.).
              Vendors pick exactly one. If none fit, they choose &quot;Other&quot; and supply their own short description.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh">
              <IconButton onClick={load}><RefreshIcon /></IconButton>
            </Tooltip>
            <Button variant="contained" onClick={openCreate}>
              Add category
            </Button>
          </Stack>
        </Stack>

        {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
        {info && <Alert severity="success" onClose={() => setInfo(null)} sx={{ mb: 2 }}>{info}</Alert>}

        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Sort</TableCell>
                <TableCell align="right">Vendors</TableCell>
                <TableCell align="center">Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Box sx={{ py: 3 }}><CircularProgress size={24} /></Box>
                  </TableCell>
                </TableRow>
              )}
              {!loading && list.map((cat) => (
                <TableRow key={cat.id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {cat.name}
                      </Typography>
                      {cat.is_system && (
                        <Tooltip title="System category - name and deletion are locked so the vendor 'Other' flow keeps working.">
                          <LockIcon fontSize="small" color="action" />
                        </Tooltip>
                      )}
                      <Chip size="small" variant="outlined" label={cat.slug} />
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {cat.description ? (
                      <Tooltip
                        title={cat.description}
                        arrow
                        slotProps={{
                          tooltip: { sx: { maxWidth: 360, whiteSpace: 'pre-wrap' } },
                        }}
                      >
                        <IconButton size="small" aria-label="View description">
                          <InfoOutlinedIcon fontSize="small" color="action" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.disabled">—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{cat.sort_order}</TableCell>
                  <TableCell align="right">{cat.vendor_count || 0}</TableCell>
                  <TableCell align="center">
                    <Switch
                      size="small"
                      checked={!!cat.is_active}
                      onChange={() => handleToggleActive(cat)}
                      disabled={cat.is_system}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => openDetails(cat)}
                      sx={{ mr: 1, verticalAlign: 'middle' }}
                    >
                      View details
                    </Link>
                    <Tooltip title="Edit sort order and visibility">
                      <IconButton size="small" onClick={() => openEdit(cat)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={cat.is_system ? 'System categories cannot be deleted' : 'Delete'}>
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(cat)}
                          disabled={cat.is_system}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && list.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">No categories yet. Add one to get started.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </Container>

      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          Category details
          {detailsCat?.is_system && (
            <Chip size="small" icon={<LockIcon />} label="system" sx={{ ml: 1 }} />
          )}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              required
              value={detailsForm.name}
              onChange={(e) => setDetailsForm({ ...detailsForm, name: e.target.value })}
              placeholder="e.g. Wood Working"
              disabled={!!detailsCat?.is_system}
              helperText={detailsCat?.is_system ? 'The "Other" category name is locked.' : 'Shown to vendors in the dropdown.'}
            />
            <TextField
              label="Description"
              value={detailsForm.description}
              onChange={(e) => setDetailsForm({ ...detailsForm, description: e.target.value })}
              multiline
              rows={4}
              placeholder="A short description shown to vendors so they know which category fits their craft."
              helperText="Hover the info icon in the table to preview this text."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)} disabled={detailsSaving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleDetailsSave}
            disabled={detailsSaving || !detailsForm.name.trim()}
          >
            {detailsSaving ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {editing ? `Settings — ${editing.name}` : 'Add category'}
          {editing?.is_system && (
            <Chip size="small" icon={<LockIcon />} label="system" sx={{ ml: 1 }} />
          )}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {!editing && (
              <>
                <TextField
                  label="Name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Wood Working"
                  helperText="Shown to vendors in the dropdown."
                />
                <TextField
                  label="Description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  multiline
                  rows={3}
                  placeholder="A short description shown to vendors so they know which category fits their craft."
                />
              </>
            )}
            <Stack direction="row" spacing={2}>
              <TextField
                label="Sort order"
                type="number"
                value={form.sortOrder}
                onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
                sx={{ width: 160 }}
                helperText="Lower = higher in the list."
              />
              <Stack direction="row" alignItems="center" spacing={1}>
                <Switch
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  disabled={!!editing?.is_system}
                />
                <Typography variant="body2">{form.isActive ? 'Active' : 'Hidden from vendors'}</Typography>
              </Stack>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || (!editing && !form.name.trim())}
          >
            {saving ? 'Saving…' : (editing ? 'Save settings' : 'Create category')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CategoriesPage;
